import { useReduxState } from 'hooks';

import type { Achievement } from 'shared-types';
import type { IconProp } from 'components/common/icons/Icon';

type AchievementTypeInfo = {
  backgroundColor: string;
  color: string;
  hasCompletion: boolean;
  hasCompletionDetails: boolean;
  hasExpiration: boolean;
  hasStartedAt: boolean;
  hasStatus: boolean;
  hasTemplate: boolean;
  icon: IconProp;
  isGroupAchievement: boolean;
  supervisorAddedMessage: string;
  supervisorAddedSubject: string;
  supportClearingItems: boolean;
  supportClosing: boolean;
  supportConfidential: boolean;
  templateHeader: string;
  timelineTitle: string;
  title: string;
};

type AchievementTypes = Record<Achievement['type'], AchievementTypeInfo>;

// overloads
function useAchievementTypes(params: {
  type: Achievement['type'];
}): AchievementTypeInfo;
function useAchievementTypes(): AchievementTypes;

// implementation
function useAchievementTypes({ type }: { type?: Achievement['type'] } = {}):
  | AchievementTypeInfo
  | AchievementTypes {
  const { messages } = useReduxState();

  const fallback: AchievementTypeInfo = {
    backgroundColor: '',
    color: '',
    hasCompletion: false,
    hasCompletionDetails: false,
    hasExpiration: false,
    hasStartedAt: false,
    hasStatus: false,
    hasTemplate: false,
    icon: '' as IconProp,
    isGroupAchievement: false,
    supervisorAddedMessage: '',
    supervisorAddedSubject: '',
    supportClearingItems: false,
    supportClosing: false,
    supportConfidential: false,
    templateHeader: '',
    timelineTitle: '',
    title: ''
  };

  const configByAchievementType: AchievementTypes = {
    external: {
      backgroundColor: '#cfc2b7',
      color: '#baa898',
      hasCompletion: true,
      hasCompletionDetails: true,
      hasExpiration: true,
      hasStartedAt: true,
      hasStatus: false,
      hasTemplate: false,
      icon: 'award',
      isGroupAchievement: false,
      supervisorAddedMessage:
        messages.achievementSupervisorAddedToAchievementMessage,
      supervisorAddedSubject: messages.achievementSupervisorAddedToAchievement,
      supportClearingItems: false,
      supportClosing: false,
      supportConfidential: false,
      templateHeader: '',
      timelineTitle: messages.achievementTypeExternalTimeline,
      title: messages.achievementTypeExternal
    },
    conversation: {
      backgroundColor: '#83bbea',
      color: '#4d9de0',
      hasCompletion: false,
      hasCompletionDetails: false,
      hasExpiration: false,
      hasStartedAt: false,
      hasStatus: true,
      hasTemplate: true,
      icon: 'comments',
      isGroupAchievement: false,
      supervisorAddedMessage:
        messages.achievementSupervisorAddedToConversationNotesMessage,
      supervisorAddedSubject:
        messages.achievementSupervisorAddedToConversationNotes,
      supportClearingItems: true,
      supportClosing: true,
      supportConfidential: true,
      templateHeader: messages.commonQuestions,
      timelineTitle: messages.achievementTypeConversationTimeline,
      title: messages.achievementTypeConversation
    },
    'group-conversation': {
      backgroundColor: '#a8d5ba',
      color: '#57a773',
      hasCompletion: false,
      hasCompletionDetails: false,
      hasExpiration: false,
      hasStartedAt: false,
      hasStatus: true,
      hasTemplate: true,
      icon: 'users',
      isGroupAchievement: true,
      supervisorAddedMessage:
        messages.achievementSupervisorAddedToGroupConversationMessage,
      supervisorAddedSubject:
        messages.achievementSupervisorAddedToGroupConversation,
      supportClearingItems: true,
      supportClosing: true,
      supportConfidential: true,
      templateHeader: messages.commonQuestions,
      timelineTitle: messages.achievementTypeGroupConversationTimeline,
      title: messages.achievementTypeGroupConversation
    },
    milestone: {
      backgroundColor: '#ebd06f',
      color: '#e1bc29',
      hasCompletion: true,
      hasCompletionDetails: false,
      hasExpiration: false,
      hasStartedAt: false,
      hasStatus: false,
      hasTemplate: false,
      icon: 'trophy',
      isGroupAchievement: false,
      supervisorAddedMessage:
        messages.achievementSupervisorAddedToMilestoneMessage,
      supervisorAddedSubject: messages.achievementSupervisorAddedToMilestone,
      supportClearingItems: false,
      supportClosing: false,
      supportConfidential: false,
      templateHeader: '',
      timelineTitle: messages.achievementTypeMilestoneTimeline,
      title: messages.achievementTypeMilestone
    },
    tasklist: {
      backgroundColor: '#af8cb6',
      color: '#8d5a97',
      hasCompletion: false,
      hasCompletionDetails: false,
      hasExpiration: false,
      hasStartedAt: false,
      hasStatus: true,
      hasTemplate: true,
      icon: 'clipboard-list',
      isGroupAchievement: false,
      supervisorAddedMessage:
        messages.achievementSupervisorAddedToTaskListMessage,
      supervisorAddedSubject: messages.achievementSupervisorAddedToTaskList,
      supportClearingItems: false,
      supportClosing: false,
      supportConfidential: false,
      templateHeader: messages.tasks,
      timelineTitle: messages.achievementTypeTasklistTimeline,
      title: messages.achievementTypeTasklist
    }
  };

  return type
    ? configByAchievementType[type] || fallback
    : configByAchievementType;
}

export default useAchievementTypes;
